/**
 * All query keys are arrays generated from simple functions. It makes easy to find
 * any cached data by query key. For example:
 *
 * When fething data from API we can set the queryKey as:
 * ```
 * getMyListQueryKey(filter1, filter2)
 * ```
 *
 * Then, when we need to find all entries from the cache for that specific queryKey,
 * we can do the following:
 * ```
 * const cachedResultsKey = getMyListQueryKey() // all data
 * const cachedResultsKey = getMyListQueryKey(filter1) // data filtered by filter1
 * const cachedResultsKey = getMyListQueryKey(filter1, filter2) // exact match
 * ```
 */

import { Address, ShipmentOutput } from '@qogita/canary-types';

type UserKeys = [];
type SaleKeys = [saleQid?: string];
type OfferKeys = [offerQid?: string];
type TokenStatusKeys = [token?: string];
type SaleLinesKeys = [saleQid?: string, page?: number];
type CarrierListKey = [];
type BankInfoKeys = [];
type AddressListKeys = [
  type?: NonNullable<Address['usageTypes']>[0],
  page?: number,
];
type ShipmentsKeys = [page: string, searchValue: string | undefined];
type OffersKeys = [page: number, pricingPremiumBucket?: string];
type SearchKeys = [search: string];

type ShipmentGroupAllKeys = [
  search_destination?: ShipmentOutput['destinationType'][],
  page?: string,
];

type SalesKeys = [
  page: number,
  status: string,
  hasRead?: boolean,
  expiredFrom?: string,
  order?: 'expired_at' | '-expired_at',
];
type TaskInitialCheck = [taskId: string];
type SellerLeadKeys = [slug?: string];
type SellerLeadListKeys = [];
type ClaimSaleProcessingDetailListKeys = [page?: number, status?: string[]];
type SellerRecommendationsKeys = [];
type SellerRecommendationsWatchlistedKeys = [];
type SellerStatsKeys = [];
type OfferPricingSummaryKeys = [];

export const getPrivateQueryKey = () => ['private'];
const getPublicQueryKey = () => ['public'];

export const getSaleDeclineReasonsKey = () => [
  ...getPublicQueryKey(),
  'saleDeclineReasons',
];

export const getUserQueryKey = (...keys: UserKeys) => [
  ...getPrivateQueryKey(),
  'user',
  ...keys,
];

export const getAddressListQueryKey = (...keys: AddressListKeys) => [
  ...getPrivateQueryKey(),
  'addressList',
  ...keys,
];

export const getOffersInfoQueryKey = (...keys: UserKeys) => [
  ...getPrivateQueryKey(),
  'offers',
  'info',
  ...keys,
];

export const getInfoQueryKey = () => [...getPublicQueryKey(), 'info'];
export const getSaleQueryKey = (...keys: SaleKeys) => [
  ...getPrivateQueryKey(),
  'sale',
  ...keys,
];
export const getSaleDocumentsQueryKey = (...keys: SaleKeys) => [
  ...getPrivateQueryKey(),
  'saleDocuments',
  ...keys,
];
export const getSaleLinesQueryKey = (...keys: SaleLinesKeys) => [
  ...getPrivateQueryKey(),
  'saleLines',
  ...keys,
];
export const getShippingCarrierListQueryKey = (...keys: CarrierListKey) => [
  ...getPublicQueryKey(),
  'carrierList',
  ...keys,
];
export const getBankInfoQueryKey = (...keys: BankInfoKeys) => [
  ...getPrivateQueryKey(),
  'bankInfo',
  ...keys,
];

export const getOfferQueryKey = (...keys: OfferKeys) => [
  ...getPrivateQueryKey(),
  'offer',
  ...keys,
];

export const getTokenStatusQueryKey = (...keys: TokenStatusKeys) => [
  ...getPublicQueryKey(),
  'token-status',
  ...keys,
];

export const getShipmentsQueryKey = (...keys: ShipmentsKeys) => [
  ...getPrivateQueryKey(),
  'shipments',
  ...keys,
];

export const getShipmentsOrdersQueryKey = (...keys: ShipmentsKeys) => [
  ...getPrivateQueryKey(),
  'shipmentsOrders',
  ...keys,
];

export const getShipmentGroupAllQueryKey = (...keys: ShipmentGroupAllKeys) => [
  ...getPrivateQueryKey(),
  'shipmentGroupAll',
  ...keys,
];

export const getShipmentGroupIdQueryKey = (...keys: [groupId: string]) => [
  ...getPrivateQueryKey(),
  'shipmentGroupAll',
  ...keys,
];

export const getWarehouseCommercialInvoiceQueryKey = (
  ...keys: [qid: string]
) => [...getPrivateQueryKey(), 'warehouseCommercialInvoice', ...keys];

export const getOffersQueryKey = (...keys: OffersKeys) => [
  ...getPrivateQueryKey(),
  'offers',
  ...keys,
];

export const getSearchQueryKey = (...keys: SearchKeys) => [
  ...getPrivateQueryKey(),
  'searchOffersAndSales',
  ...keys,
];

export const getOrdersQueryKey = (...keys: SalesKeys) => [
  ...getPrivateQueryKey(),
  'orders',
  ...keys,
];
export const getOrdersQueryKeyPartial = (...keys: Partial<SalesKeys>) =>
  getOrdersQueryKey(...(keys as SalesKeys));

export const getTaskInitialCheck = (...keys: TaskInitialCheck) => [
  ...getPrivateQueryKey(),
  'taskInitialCheck',
  ...keys,
];

export const getSellerLeadQueryKey = (...keys: SellerLeadKeys) => [
  ...getPrivateQueryKey(),
  'sellerLead',
  ...keys,
];

export const getSellerLeadsQueryKey = (...keys: SellerLeadListKeys) => [
  ...getPrivateQueryKey(),
  'SellerLeadListKeys',
  ...keys,
];

export const getClaimSaleProcessingDetailListKey = (
  ...keys: ClaimSaleProcessingDetailListKeys
) => [...getPrivateQueryKey(), 'ClaimSaleProcessingDetailList', ...keys];

export const SellerRecommendationsKey = (
  ...keys: SellerRecommendationsKeys
) => [...getPrivateQueryKey(), 'SellerRecommendations', ...keys];

export const SellerRecommendationsWatchlistedKey = (
  ...keys: SellerRecommendationsWatchlistedKeys
) => [...getPrivateQueryKey(), 'SellerRecommendationsWatchlistedKeys', ...keys];

export const getSellerStatsKey = (...keys: SellerStatsKeys) => [
  ...getPrivateQueryKey(),
  'sellerMetrics',
  ...keys,
];

export const getOfferPricingSummaryKey = (...keys: OfferPricingSummaryKeys) => [
  ...getPrivateQueryKey(),
  'offerPricingSummary',
  ...keys,
];

export const getSaleCarrierBookingKey = (saleQid: string) => [
  ...getPrivateQueryKey(),
  'saleCarrierBooking',
  saleQid,
];

export const sessionKey = ['session'];
